<script setup lang="ts" generic="T">
import Map from '~/ui/Map.vue'
import type { Ref } from 'vue'

/**
* Use a composable directly inside the template, avoiding an explicit `computed` or `watch` setup
*
* **Use** for low-priority UI, or for small template fragments that are coupled with some external data sink/source
* **Use** to add a data source to a writable scoped slot-prop directly in the template
* **Don't use** for essential data that is required at first page load - use a module-wide `computed` instead
*
* - Use the slot :state to write to the current composable state. Use :value for a read-only value.
* - (optional) Add a v-model to be overwritten by the composable
* - Allows data to be scoped with a fragment of the template to avoid over-fetching and name collisions
*
* @example
* <Sync
*   v-if="track.artist_credit?.[0]"
*   :use="() => useDataStore().get('artist', track.artist_credit[0]!.artist.id)"
*   v-slot="{ value: artist }"
* >
*
*/
const props = defineProps<{
  // Thunk that returns a Ref of type T
  use: () => Ref<T>
}>()

const model = defineModel<T>()

const composableState = props.use()
</script>

<template>
  <Map
    v-slot="{ state, value }"
    v-model="model"
    :in="(_) => composableState /* Expose to slot.state */"
    :out="(internal) => composableState = internal /* Push changes from slot.state to composable */"
  >
    <slot :state :value />
  </Map>
</template>
